import React, { createContext, useContext, useState, useEffect } from 'react';
import { doc, getDoc, updateDoc, setDoc, deleteDoc, deleteField, increment, serverTimestamp, writeBatch } from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';
import { AuthContext } from '../Auth/AuthContext';
import { testFirestorePermissions, checkUserType, createUserDocumentIfMissing, debugUserAuthState } from './CheckoutDebug';
import { withRetry, isNetworkError } from '../utils/networkUtils';
import { NetworkContext } from './NetworkContext';
import { calculateDeliveryInfo } from '../utils/DeliveryHelper';

export const CartContext = createContext();

// Debug logging utility - only logs in development
const debugLog = (message, ...args) => {
  if (process.env.NODE_ENV === 'development') {
    console.log(message, ...args);
  }
};

const debugError = (message, ...args) => {
  if (process.env.NODE_ENV === 'development') {
    console.error(message, ...args);
  }
};

export const CartProvider = ({ children }) => {
  const { user, userType } = useContext(AuthContext);
  const { isOnline } = useContext(NetworkContext);
  const [cartItems, setCartItems] = useState([]);
  const [inventoryLevels, setInventoryLevels] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Helper: check if an order is assigned to the given user id supporting multiple field names
  const isAssignedTo = (orderData, uid) => {
    if (!orderData || !uid) return false;
    return (
      orderData.deliveryPersonId === uid ||
      orderData.deliveryPerson === uid ||
      orderData.assignedTo === uid ||
      orderData.deliveryPersonUid === uid
    );
  };
  
  // Load initial cart and inventory levels
  useEffect(() => {
    if (!user) {
      setCartItems([]);
      setInventoryLevels({});
      return;
    }
    
    const loadCartAndInventory = async () => {
      setLoading(true);
      setError(null);
      
      if (!isOnline) {
        debugLog("Network is offline. Using cached cart data if available.");
      }
      
      try {
        // Get cart items
        const cartRef = doc(db, 'incart', user.uid);
        
        // Use withRetry utility for network resilience
        const cartSnap = await withRetry(
          () => getDoc(cartRef), 
          { maxRetries: 3 }
        );
           if (cartSnap.exists()) {
        const itemMap = cartSnap.data().items || {};
        const itemEntries = Object.entries(itemMap);
        
        const itemPromises = itemEntries.map(async ([key, data]) => {
          try {
            const snap = await getDoc(data.ref);
            if (!snap.exists()) return null;
            
            // Use productId and variant from stored data instead of parsing key
            // This prevents issues with product IDs containing underscores
            const itemId = data.productId || key.split('_')[0]; // Fallback for old cart items
            const variant = data.variant || key.substring(key.indexOf('_') + 1); // Fallback for old cart items
            const productData = snap.data();
            const { group, subgroup } = productData;
            
            // Check if this is a loose item
            const isLooseItem = data.loose_item || productData.loose_item || false;
            debugLog(`[DEBUG] Loading cart item ${key}, loose item: ${isLooseItem}`);
            
            let available = 0;
            
            if (isLooseItem) {
              // This is a loose item, always get available from the main product
              available = parseFloat(productData.available || 0);
              debugLog(`[DEBUG] Loose item ${itemId}, variant: ${variant}, available: ${available} ${productData.availableUnit || 'kg'}`);
            } else if (productData.variants && productData.variants[variant]) {
              // This is a variant product (not loose item), get its specific availability
              available = parseInt(productData.variants[variant].available || 0);
            } else {
              // This is a standard product (not loose item), use the main availability field
              available = parseInt(productData.available || 0);
            }
                 // Track inventory for this item
            setInventoryLevels(prev => ({
              ...prev,
              [key]: available
            }));
            
            // Additional data for loose items
            let looseItemData = {};
            let variantUnit = '';
            
            if (isLooseItem) {
              // Extract weight from variant
              const variantWeight = extractWeightFromVariant(variant);
              
              // Extract unit from variant
              variantUnit = productData.variants?.[variant]?.unit || extractUnitFromVariant(variant);
              
              // Get product unit
              const productUnit = productData.availableUnit || 'gram';
              
              // Calculate how many units of this variant are available
              let availableInVariantUnits = available;
              
              if (variantUnit !== productUnit) {
                // Convert available from product unit to variant unit
                availableInVariantUnits = convertWeight(available, productUnit, variantUnit);
              }
              
              // Calculate how many full units of this variant can be purchased
              const possibleUnits = Math.floor(availableInVariantUnits / variantWeight);
              
              looseItemData = {
                variantWeight,
                variantUnit,
                productUnit,
                availableInVariantUnits: possibleUnits
              };
              
              console.log(`[DEBUG] Loose item ${itemId} (${variant}): ${variantWeight} ${variantUnit} per unit`);
              console.log(`[DEBUG] Available: ${available} ${productUnit}, can make: ${possibleUnits} units of ${variant}`);
            } else {
              // For regular items, get the unit from variants if available
              if (productData.variants && productData.variants[variant] && productData.variants[variant].unit) {
                variantUnit = productData.variants[variant].unit;
              }
              // Also check if variantUnit was previously stored in cart data
              if (!variantUnit && data.variantUnit) {
                variantUnit = data.variantUnit;
              }
            }
            
            return {
              itemKey: key,
              id: itemId,
              variant: variant,
              qty: data.qty || 1,
              weight: data.weight || variant,
              price: parseFloat(data.price) || 0,
              mrp: parseFloat(data.mrp) || 0,
              group,
              subgroup,
              available,
              hasVariants: !!productData.variants,
              loose_item: isLooseItem,
              variantUnit, // Add unit information for all items
              ...looseItemData,
              ...snap.data()
            };
            } catch (err) {
              debugError(`Error loading cart item ${key}:`, err);
              return null;
            }
          });
          
          const items = await Promise.all(itemPromises);
          setCartItems(items.filter(Boolean));
        }
      } catch (err) {
        console.error('Error loading cart:', err);
      } finally {
        setLoading(false);
      }
    };
    
    loadCartAndInventory();
  }, [user]);
  
  // Check availability of a specific product/variant
  const checkAvailability = (product, variant) => {
    // This is used in product pages, so we keep it
    if (product.variants && product.variants[variant]) {
      // This is a variant product
      return parseInt(product.variants[variant].available || 0);
    }
    // This is a standard product
    return parseInt(product.available || 0);
  };
  
  // Calculate weight in common unit (grams) for loose items
  const calculateWeightInCommonUnit = (weight, unit) => {
    // Convert everything to grams for calculation
    return convertWeight(weight, unit || 'gram', 'gram');
  };
  
  // Convert weight between units (kg/gram)
  const convertWeight = (weight, fromUnit, toUnit) => {
    // Handle invalid weight value
    if (weight === null || weight === undefined || isNaN(weight)) {
      console.warn('[DEBUG] Invalid weight value in convertWeight:', weight);
      return 0;
    }
    
    // Convert weight to number to ensure proper calculation
    weight = parseFloat(weight) || 0;
    
    // Ensure fromUnit and toUnit are strings before calling toLowerCase()
    if (typeof fromUnit !== 'string') {
      console.warn('[DEBUG] fromUnit is not a string:', fromUnit);
      fromUnit = 'gram';
    } else {
      try {
        fromUnit = (fromUnit || 'gram').toLowerCase();
      } catch (err) {
        console.warn('[DEBUG] Error normalizing fromUnit:', fromUnit, err);
        fromUnit = 'gram';
      }
    }
    
    if (typeof toUnit !== 'string') {
      console.warn('[DEBUG] toUnit is not a string:', toUnit);
      toUnit = 'gram';
    } else {
      try {
        toUnit = (toUnit || 'gram').toLowerCase();
      } catch (err) {
        console.warn('[DEBUG] Error normalizing toUnit:', toUnit, err);
        toUnit = 'gram';
      }
    }
    
    // If units are the same, no conversion needed
    if (fromUnit === toUnit) {
      return weight;
    }
    
    // Define conversion factors to grams
    const toGrams = {
      'mg': 0.001,
      'milligram': 0.001,
      'g': 1,
      'gram': 1,
      'gm': 1,
      'kg': 1000,
      'kilogram': 1000,
      'lb': 453.592,
      'pound': 453.592,
      'oz': 28.3495,
      'ounce': 28.3495
    };
    
    // Define conversion factors to milliliters
    const toMl = {
      'ml': 1,
      'milliliter': 1,
      'l': 1000,
      'liter': 1000,
      'litre': 1000,
      'gallon': 3785.41,
      'fl oz': 29.5735,
      'fluid ounce': 29.5735
    };
    
    // Check if we're converting between weight units
    if (toGrams[fromUnit] && toGrams[toUnit]) {
      // Convert from source unit to grams, then to target unit
      return (weight * toGrams[fromUnit]) / toGrams[toUnit];
    }
    
    // Check if we're converting between volume units
    if (toMl[fromUnit] && toMl[toUnit]) {
      // Convert from source unit to ml, then to target unit
      return (weight * toMl[fromUnit]) / toMl[toUnit];
    }
    
    // If units are incompatible or unknown, log warning and return original
    console.warn(`[DEBUG] Cannot convert between units: ${fromUnit} to ${toUnit}`);
    return weight;
  };
  
  // Helper function to extract weight from variant string (e.g., "2kg" -> 2)
  const extractWeightFromVariant = (variant) => {
    // Handle null, undefined, or invalid inputs
    if (variant === null || variant === undefined) return 1;
    
    // Special case for default variant
    if (variant === "default") {
      return 1;
    }
    
    try {
      // Convert to string safely
      const variantStr = String(variant);
      
      // Match numbers (including decimals) at the start of the string
      const match = variantStr.match(/^(\d*\.?\d+)/);
      if (match && match[1]) {
        return parseFloat(match[1]);
      }
    } catch (err) {
      console.error('[DEBUG] Error extracting weight from variant:', err);
    }
    
    return 1; // Default to 1 if no number found or error
  };
  
  // Helper function to extract unit from variant string (e.g., "2kg" -> "kg")
  const extractUnitFromVariant = (variant) => {
    // Handle null, undefined, or invalid inputs
    if (variant === null || variant === undefined) {
      console.warn('[DEBUG] Null or undefined variant in extractUnitFromVariant');
      return 'piece';
    }
    
    // Special case for default variant
    if (variant === "default") {
      return 'piece';
    }
    
    try {
      // Convert to string safely
      const variantStr = String(variant);
      
      // Match letters after numbers
      const match = variantStr.match(/^\d*\.?\d+(.*)/);
      if (match && match[1]) {
        const unit = match[1].trim();
        return unit || 'piece';
      } else {
        console.warn(`[DEBUG] Could not extract unit from variant: "${variantStr}"`);
      }
    } catch (err) {
      console.error('[DEBUG] Error extracting unit from variant:', err);
    }
    
    return 'piece'; // Default to piece if no unit found or error
  };
  
  // Add to cart with inventory check
  const addToCart = async (product, variant, quantity = 1) => {
    if (!user) {
      alert('Please log in to add items to your cart');
      return false;
    }
    
    // Check if we're offline
    if (!isOnline) {
      alert('You are currently offline. Please connect to the internet to add items to your cart.');
      return false;
    }

    // Validate inputs
    if (!validateQuantity(quantity)) {
      alert('Please enter a valid quantity (1-1000)');
      return false;
    }

    if (!product || !variant) {
      alert('Invalid product or variant');
      return false;
    }
    
    setLoading(true);
    try {
      debugLog(`[DEBUG] Adding to cart: product=${product.name}, variant=${variant}, qty=${quantity}`);
      
      const itemKey = `${product.id}_${variant}`;
      const currentInCart = parseFloat(cartItems.find(item => item.itemKey === itemKey)?.qty || 0);
      
      // Check if this is a loose item (sold by weight)
      const isLooseItem = product.loose_item || false;
      console.log(`[DEBUG] Is loose item: ${isLooseItem}`);
      
      // Check if product has variants
      let available = 0;
      
      // For loose items, always get available from the main product
      if (isLooseItem) {
        available = parseFloat(product.available || 0);
        console.log(`[DEBUG] Loose item, available: ${available} ${product.availableUnit || 'gram'}`);
      } else if (product.variants && product.variants[variant]) {
        // This is a variant product (not loose item)
        available = parseFloat(product.variants[variant].available || 0);
        console.log(`[DEBUG] Variant product, available: ${available}`);
      } else {
        // This is a standard product (not loose item)
        available = parseFloat(product.available || 0);
        console.log(`[DEBUG] Standard product, available: ${available}`);
      }
      
      // For loose items, we need to calculate how much weight is being requested
      if (isLooseItem) {
        // Extract weight and unit from the variant
        let variantWeight = 0;
        let variantUnit = 'gram';
        
        if (product.variants && product.variants[variant]) {
          // Get unit from product data if available
          variantUnit = product.variants[variant].unit || variantUnit;
          
          // Extract weight from the variant using our helper
          variantWeight = extractWeightFromVariant(variant);
          
          // If unit wasn't found in product data, try to extract from variant string
          if (!product.variants[variant].unit) {
            variantUnit = extractUnitFromVariant(variant);
          }
        } else {
          variantWeight = extractWeightFromVariant(variant);
          variantUnit = extractUnitFromVariant(variant);
        }
        
        // Get product unit
        const productUnit = product.availableUnit || 'gram';
        
        console.log(`[DEBUG] Variant weight: ${variantWeight}, unit: ${variantUnit}`);
        console.log(`[DEBUG] Product unit: ${productUnit}, available: ${available}`);
        
        // Calculate total weight being requested
        let requestedWeight = variantWeight * quantity;
        let currentInCartWeight = variantWeight * currentInCart;
        
        console.log(`[DEBUG] Requested: ${requestedWeight} ${variantUnit}, already in cart: ${currentInCartWeight} ${variantUnit}`);
        
        // Convert to product unit if needed
        let requestedWeightInProductUnit = requestedWeight;
        let currentInCartWeightInProductUnit = currentInCartWeight;
        
        if (variantUnit !== productUnit) {
          requestedWeightInProductUnit = convertWeight(requestedWeight, variantUnit, productUnit);
          currentInCartWeightInProductUnit = convertWeight(currentInCartWeight, variantUnit, productUnit);
          console.log(`[DEBUG] Converted requested weight: ${requestedWeight} ${requestedWeightInProductUnit} ${productUnit}`);
          console.log(`[DEBUG] Converted cart weight: ${currentInCartWeight} ${currentInCartWeightInProductUnit} ${productUnit}`);
        }
        
        // Check if adding this quantity would exceed available weight
        if (currentInCartWeightInProductUnit + requestedWeightInProductUnit > available) {
          // Calculate maximum quantity possible
          const remainingWeightInProductUnit = available - currentInCartWeightInProductUnit;
          const remainingWeightInVariantUnit = (variantUnit !== productUnit) 
            ? convertWeight(remainingWeightInProductUnit, productUnit, variantUnit)
            : remainingWeightInProductUnit;
            
          const possibleToAdd = Math.floor(remainingWeightInVariantUnit / variantWeight);
          
          console.log(`[DEBUG] Not enough inventory. Remaining: ${remainingWeightInProductUnit} ${productUnit}`);
          console.log(`[DEBUG] Can add at most: ${possibleToAdd} × ${variant}`);
          
          if (possibleToAdd <= 0) {
            alert(`Sorry, this product is out of stock`);
            setLoading(false);
            return false;
          }
          
          quantity = possibleToAdd;
          alert(`Only ${possibleToAdd} ${variant} available. Added ${possibleToAdd} to your cart.`);
        }
      } else {
        // Regular item logic (not weight-based)
        // Parse quantities as numbers to ensure proper comparison
        const parsedQuantity = parseFloat(quantity) || 0;
        const parsedAvailable = parseFloat(available) || 0;
        const parsedCurrentInCart = parseFloat(currentInCart) || 0;
        
        console.log(`[DEBUG] Regular item - requested: ${parsedQuantity}, in cart: ${parsedCurrentInCart}, available: ${parsedAvailable}`);
        
        // Check if adding this quantity would exceed available inventory
        if (parsedCurrentInCart + parsedQuantity > parsedAvailable) {
          // Add the maximum possible
          const possibleToAdd = Math.max(0, parsedAvailable - parsedCurrentInCart);
          
          console.log(`[DEBUG] Not enough inventory. Can add at most: ${possibleToAdd}`);
          
          if (possibleToAdd <= 0) {
            alert(`Sorry, this product is out of stock`);
            setLoading(false);
            return false;
          }
          
          quantity = possibleToAdd;
          alert(`Only ${parsedAvailable} items available. Added ${possibleToAdd} to your cart.`);
        }
      }
      
      // Get product reference
      const productRef = doc(db, 'store', 'dhanaura', 'groups', product.group, 'subgroups', product.subgroup, 'products', product.id);
      
      // Get variant price and MRP or default price
      // Ensure price is always a valid number
      let price = 0;
      let mrp = 0;
      let variantUnit = '';
      
      if (product.variants && product.variants[variant]) {
        price = parseFloat(product.variants[variant].price) || 0;
        mrp = parseFloat(product.variants[variant].mrp) || 0;
        variantUnit = product.variants[variant].unit || '';
      } else if (product.wSale !== undefined) {
        price = parseFloat(product.wSale) || 0;
        mrp = parseFloat(product.mrp) || 0;
      }
      
      console.log(`[DEBUG] Price for ${product.name}, variant ${variant}: ${price}, MRP: ${mrp}, Unit: ${variantUnit}`);
      
      // Prepare cart item data
      const itemData = {
        ref: productRef,
        productId: product.id, // Store product ID separately (like Android)
        variant: variant, // Store variant separately (like Android)
        qty: parseFloat(quantity) || 1,
        weight: variant,
        price: parseFloat(price) || 0,
        mrp: parseFloat(mrp) || 0,
        hasVariants: !!product.variants,
        loose_item: isLooseItem,
        variantUnit: variantUnit // Store unit information
      };
      
      // Update Firebase cart
      const cartRef = doc(db, 'incart', user.uid);
      const cartSnap = await getDoc(cartRef);
      
      if (cartSnap.exists()) {
        // Update existing cart
        await updateDoc(cartRef, {
          [`items.${itemKey}`]: itemData
        });
      } else {
        // Create new cart
        await setDoc(cartRef, {
          items: {
            [itemKey]: itemData
          }
        });
      }
      
      // Update local state
      const existingItemIndex = cartItems.findIndex(item => item.itemKey === itemKey);
      
      if (existingItemIndex >= 0) {
        // Update existing item
        const updatedItems = [...cartItems];
        updatedItems[existingItemIndex] = {
          ...updatedItems[existingItemIndex],
          qty: parseFloat(quantity) || 1,
          price: parseFloat(price) || 0,
          mrp: parseFloat(mrp) || 0,
          variantUnit: variantUnit // Include unit information
        };
        setCartItems(updatedItems);
        console.log(`[DEBUG] Updated existing item in cart, new qty: ${quantity}`);
      } else {
        // Add new item
        const newItem = {
          itemKey,
          id: product.id,
          variant,
          qty: parseFloat(quantity) || 1,
          weight: variant,
          price: parseFloat(price) || 0,
          mrp: parseFloat(mrp) || 0,
          name: product.name,
          about: product.about,
          imageData: product.imageData,
          group: product.group,
          subgroup: product.subgroup,
          available: parseFloat(available) || 0,
          hasVariants: !!product.variants,
          loose_item: isLooseItem,
          variantUnit: variantUnit // Include unit information
        };
        setCartItems([...cartItems, newItem]);
        console.log(`[DEBUG] Added new item to cart: ${product.name}, variant: ${variant}, qty: ${quantity}`);
      }
      
      // Update local inventory tracking
      setInventoryLevels(prev => ({
        ...prev,
        [itemKey]: parseFloat(available) - parseFloat(quantity)
      }));
      
      return true;
    } catch (err) {
      console.error('[DEBUG] Error adding to cart:', err);
      alert('Error adding item to cart: ' + (err.message || 'Unknown error'));
      return false;
    } finally {
      setLoading(false);
    }
  };
  
  // Update quantity in cart
  const updateCartQuantity = async (itemKey, delta) => {
    if (!user) return false;
    
    // Check if we're offline
    if (!isOnline) {
      alert('You are currently offline. Please connect to the internet to update your cart.');
      return false;
    }
    
    try {
      const item = cartItems.find(i => i.itemKey === itemKey);
      if (!item) {
        console.log(`[DEBUG] Item not found in cart: ${itemKey}`);
        return false;
      }
      
      // Parse as numbers to avoid string concatenation
      let newQty = Math.max(1, parseFloat(item.qty) + parseFloat(delta));
      console.log(`[DEBUG] Updating quantity for ${item.name}, variant: ${item.variant}, from ${item.qty} to ${newQty}`);
      console.log(`[DEBUG] Product path: store/dhanaura/groups/${item.group}/subgroups/${item.subgroup}/products/${item.id}`);
      console.log(`[DEBUG] Item details:`, JSON.stringify(item, null, 2));
      
      // Get the latest product data to check current availability
      const prodRef = doc(db, 'store', 'dhanaura', 'groups', item.group, 'subgroups', item.subgroup, 'products', item.id);
      const prodSnap = await getDoc(prodRef);
      if (!prodSnap.exists()) {
        console.error(`[DEBUG] Product no longer exists: ${item.name}`);
        console.error(`[DEBUG] Tried path: ${prodRef.path}`);
        console.error(`[DEBUG] Item data:`, item);
        
        // Check if itemKey suggests a different product ID
        if (item.itemKey && item.itemKey !== item.id && item.itemKey.includes('_')) {
          const possibleId = item.itemKey.split('_').slice(0, -1).join('_');
          console.error(`[DEBUG] Mismatch detected! Cart item ID is "${item.id}" but itemKey suggests ID should be "${possibleId}"`);
          alert(`Product "${item.name}" has an incorrect ID in your cart. This usually happens after an app update. Please clear your cart and add the products again.`);
        } else {
          alert(`Product "${item.name}" not found in database. The product might have been removed. Please refresh the page.`);
        }
        return false;
      }
      
      const prodData = prodSnap.data();
      let available = 0;
      
      // Check if this is a loose item
      const isLooseItem = prodData.loose_item || false;
      const isDefaultVariant = item.variant === "default";
      
      // Get available inventory
      if (isDefaultVariant) {
        // For default variant, simplify the inventory check
        available = parseFloat(prodData.available || 0);
        console.log(`[DEBUG] Default variant item, available: ${available} items`);
        
        // Check if we have enough inventory for default variant
        if (newQty > available) {
          console.log(`[DEBUG] Not enough inventory for default variant. Requested: ${newQty}, available: ${available}`);
          
          if (available <= 0) {
            alert(`Sorry, this product is out of stock`);
            return false;
          }
          
          alert(`Only ${available} items available`);
          // Use maximum available
          newQty = Math.floor(available);
        }
      } else if (isLooseItem) {
        // For loose items, always get available from the main product
        available = parseFloat(prodData.available || 0);
        console.log(`[DEBUG] Loose item, available: ${available} ${prodData.availableUnit || 'gram'}`);
        
        // Extract weight and unit from variant - with additional checks
        let variantWeight = 1;
        let variantUnit = 'piece';
        
        // Special handling for "default" variant which doesn't need weight/unit extraction
        if (item.variant === "default") {
          console.log("[DEBUG] Using default values for 'default' variant");
          variantWeight = 1;
          variantUnit = 'piece';
        } else if (item.variant) {
          try {
            variantWeight = extractWeightFromVariant(item.variant);
            
            // First try to get the unit from the product data
            if (prodData.variants && 
                prodData.variants[item.variant] && 
                typeof prodData.variants[item.variant].unit === 'string') {
              variantUnit = prodData.variants[item.variant].unit;
            } else {
              // Fall back to extracting from variant string
              variantUnit = extractUnitFromVariant(item.variant);
            }
          } catch (variantErr) {
            console.error('[DEBUG] Error processing variant:', variantErr);
            // Fallback to safe values
            variantWeight = 1;
            variantUnit = 'piece';
          }
        } else {
          console.warn(`[DEBUG] Missing variant for item: ${item.name} (${item.itemKey})`);
        }
          
        const productUnit = prodData.availableUnit || 'gram';
        
        // Calculate total weight being requested
        const totalRequestedWeight = variantWeight * newQty;
        
        // Convert to product unit if needed
        let totalRequestedWeightInProductUnit = totalRequestedWeight;
        if (variantUnit !== productUnit) {
          totalRequestedWeightInProductUnit = convertWeight(totalRequestedWeight, variantUnit, productUnit);
        }
        
        console.log(`[DEBUG] Requested weight: ${totalRequestedWeight} ${variantUnit} (${totalRequestedWeightInProductUnit} ${productUnit}), available: ${available} ${productUnit}`);
        
        // Check if we have enough inventory
        if (totalRequestedWeightInProductUnit > available) {
          // Calculate maximum possible quantity
          let availableInVariantUnit = available;
          if (variantUnit !== productUnit) {
            availableInVariantUnit = convertWeight(available, productUnit, variantUnit);
          }
          
          const possibleQty = Math.floor(availableInVariantUnit / variantWeight);
          
          console.log(`[DEBUG] Not enough inventory for loose item. Max possible: ${possibleQty} × ${item.variant}`);
          
          if (possibleQty <= 0) {
            alert(`Sorry, this product is out of stock`);
            return false;
          }
          
          if (possibleQty < newQty) {
            alert(`Only ${possibleQty} ${item.variant} available`);
            // Use maximum possible quantity
            newQty = possibleQty;
          }
        }
      } else {
        // Regular item or "default" variant on items without actual variants
        
        // Check for variants field presence first
        const hasVariantsField = prodData.variants && Object.keys(prodData.variants).length > 0;
        
        if (hasVariantsField && prodData.variants[item.variant]) {
          // Item has variants and this is a specific variant
          available = parseFloat(prodData.variants[item.variant].available || 0);
          console.log(`[DEBUG] Regular item with variant, available: ${available}`);
        } else {
          // Item either has no variants field or this is a "default" variant
          // In either case, we use the product's main available quantity
          available = parseFloat(prodData.available || 0);
          console.log(`[DEBUG] Item without variants or using default variant, available: ${available}`);
        }
        
        // Check if we have enough inventory
        if (newQty > available) {
          console.log(`[DEBUG] Not enough inventory for item. Requested: ${newQty}, available: ${available}`);
          
          if (available <= 0) {
            alert(`Sorry, this product is out of stock`);
            return false;
          }
          
          alert(`Only ${available} items available`);
          // Use maximum available
          newQty = Math.floor(available);
        }
      }
      
      // Update Firebase cart
      const cartRef = doc(db, 'incart', user.uid);
      
      await updateDoc(cartRef, {
        [`items.${itemKey}.qty`]: newQty
      });
      
      // Update local state
      setCartItems(prevItems => {
        const updatedItems = prevItems.map(item => {
          if (item.itemKey === itemKey) {
            return { ...item, qty: newQty };
          }
          return item;
        });
        
        // Save to local storage
        localStorage.setItem('cartItems', JSON.stringify(updatedItems));
        
        return updatedItems;
      });
      
      return true;
    } catch (err) {
      console.error('[DEBUG] Error updating cart quantity:', err);
      
      // Create a more descriptive error message
      let errorMessage = 'Unknown error';
      
      if (err.message) {
        errorMessage = err.message;
      } else if (err.toString) {
        errorMessage = err.toString();
      }
      
      // Log additional context
      console.error('[DEBUG] Context:', {
        itemKey,
        itemDetails: cartItems.find(i => i.itemKey === itemKey),
        delta
      });
      
      // Display a more user-friendly message
      alert('Unable to update quantity. Please try again or refresh the page.');
      
      return false;
    }
  };
  
  // Remove from cart
  const removeFromCart = async (itemKey) => {
    if (!user) return false;
    
    // Check if we're offline
    if (!isOnline) {
      alert('You are currently offline. Please connect to the internet to update your cart.');
      return false;
    }
    
    try {
      // Update Firebase with retry for network resilience
      const cartRef = doc(db, 'incart', user.uid);
      await withRetry(() => 
        updateDoc(cartRef, {
          [`items.${itemKey}`]: deleteField()
        }),
        { maxRetries: 3 }
      );
      
      // Update local state
      const updatedItems = cartItems.filter(i => i.itemKey !== itemKey);
      setCartItems(updatedItems);
      
      return true;
    } catch (err) {
      console.error('Error removing from cart:', err);
      return false;
    }
  };
  
  // Verify inventory at checkout time
  const verifyInventoryForCheckout = async () => {
    if (!user) {
      console.log('[DEBUG] No user found for inventory verification');
      return { success: false, message: 'User not authenticated' };
    }
    
    if (!cartItems || cartItems.length === 0) {
      console.log('[DEBUG] Empty cart for inventory verification');
      return { success: false, message: 'No items in cart' };
    }
    
    try {
      console.log('[DEBUG] Starting inventory verification for checkout with', cartItems.length, 'items');
      const unavailableItems = [];
      
      for (const item of cartItems) {
        try {
          if (!item) {
            console.log('[DEBUG] Invalid cart item found (null or undefined)');
            continue;
          }
          
          const itemName = item.name || 'Unknown product';
          const itemVariant = item.variant || 'default';
          const itemQty = parseFloat(item.qty) || 0;
          
          console.log(`[DEBUG] Checking item: ${itemName}, variant: ${itemVariant}, qty: ${itemQty}`);
          
          // Check if we have all required fields
          if (!item.id || !item.group || !item.subgroup) {
            console.log(`[DEBUG] Missing required fields for item: ${itemName}`);
            unavailableItems.push({
              name: itemName,
              variant: itemVariant,
              requestedQty: itemQty,
              availableQty: 0,
              reason: 'Invalid product information'
            });
            continue;
          }
          
          // Get latest product data
          const prodRef = doc(db, 'store', 'dhanaura', 'groups', item.group, 'subgroups', item.subgroup, 'products', item.id);
          const prodSnap = await getDoc(prodRef);
          
          if (!prodSnap.exists()) {
            console.log(`[DEBUG] Product no longer exists: ${itemName}`);
            unavailableItems.push({
              name: itemName,
              variant: itemVariant,
              requestedQty: itemQty,
              availableQty: 0,
              reason: 'Product no longer exists'
            });
            continue;
          }
        
        const prodData = prodSnap.data();
        let available = 0;
        
        // Check if this is a loose item
        const isLooseItem = prodData.loose_item || false;
        
        // For loose items, always get available from the main product
        if (isLooseItem) {
          available = parseFloat(prodData.available || 0);
          console.log(`[DEBUG] Loose item, available: ${available} ${prodData.availableUnit || 'gram'}`);
        } else if (prodData.variants && prodData.variants[item.variant]) {
          // This is a variant product (not loose item)
          available = parseFloat(prodData.variants[item.variant].available || 0);
          console.log(`[DEBUG] Variant product, available: ${available}`);
        } else {
          // This is a standard product (not loose item), use the main availability field
          available = parseFloat(prodData.available || 0);
          console.log(`[DEBUG] Standard product, available: ${available}`);
        }
        
        if (isLooseItem) {
          // For loose items, we need to calculate weight
          // Extract weight from variant string (e.g., "2kg" -> 2)
          let variantWeight = extractWeightFromVariant(item.variant);
          
          // Extract unit from variant string or use unit from product data
          let variantUnit = prodData.variants && prodData.variants[item.variant] && prodData.variants[item.variant].unit
            ? prodData.variants[item.variant].unit 
            : extractUnitFromVariant(item.variant);
          
          // Get product unit (the unit used for the main available field)
          const productUnit = prodData.availableUnit || 'gram';
          
          // Calculate total weight being requested
          let totalRequestedWeight = variantWeight * item.qty;
          
          // Debug logging
          console.log(`[DEBUG] Item: ${item.name}, variant: ${item.variant}`);
          console.log(`[DEBUG] Extracted weight: ${variantWeight}, unit: ${variantUnit}`);
          console.log(`[DEBUG] Product unit: ${productUnit}, available: ${available}`);
          console.log(`[DEBUG] Requested: ${totalRequestedWeight} ${variantUnit} (${item.qty} × ${variantWeight}${variantUnit})`);
          
          // Convert the requested weight to the product's unit for comparison
          try {
            if (variantUnit !== productUnit) {
              const originalWeight = totalRequestedWeight;
              totalRequestedWeight = convertWeight(totalRequestedWeight, variantUnit, productUnit);
              console.log(`[DEBUG] Converted ${originalWeight} ${variantUnit} to ${totalRequestedWeight} ${productUnit}`);
            }
          } catch (conversionErr) {
            console.error(`[DEBUG] Error converting weights: ${conversionErr.message}`);
            totalRequestedWeight = variantWeight * parseFloat(item.qty); // Fallback to direct calculation
            console.log(`[DEBUG] Using fallback weight calculation: ${totalRequestedWeight}`);
          }
          
          console.log(`[DEBUG] Final comparison: ${totalRequestedWeight} ${productUnit} requested vs ${available} ${productUnit} available`);
          
          if (totalRequestedWeight > available) {
            // Calculate how many of this variant we can provide
            let availableInVariantUnit = available;
            if (variantUnit !== productUnit) {
              availableInVariantUnit = convertWeight(available, productUnit, variantUnit);
            }
            
            // How many full units of this variant can we provide
            const possibleQty = Math.floor(availableInVariantUnit / variantWeight);
            
            console.log(`[DEBUG] Not enough inventory. Can provide: ${possibleQty} × ${item.variant}`);
            
            unavailableItems.push({
              name: item.name,
              variant: item.variant,
              requestedQty: item.qty,
              availableQty: possibleQty,
              reason: `Only ${available} ${productUnit} available (${possibleQty} × ${item.variant})`
            });
          }
        } else {
          // Regular item - ensure all numbers are properly parsed as numbers
          const itemQty = parseFloat(item.qty) || 0;
          
          console.log(`[DEBUG] Regular item comparison: requested=${itemQty}, available=${available}`);
          
          if (itemQty > available) {
            console.log(`[DEBUG] Not enough inventory for regular item`);
            unavailableItems.push({
              name: item.name || 'Unknown product',
              variant: item.variant || 'default',
              requestedQty: itemQty,
              availableQty: available,
              reason: `Only ${available} available`
            });
          }
        }
        } catch (itemErr) {
          console.error(`[DEBUG] Error checking inventory for item:`, item, itemErr);
          unavailableItems.push({
            name: item?.name || 'Unknown product',
            variant: item?.variant || 'default',
            requestedQty: parseFloat(item?.qty) || 0,
            availableQty: 0,
            reason: `Error checking inventory: ${itemErr.message || 'Unknown error'}`
          });
        }
      }
      
      if (unavailableItems.length > 0) {
        console.log(`[DEBUG] Inventory verification failed. Unavailable items:`, unavailableItems);
        return {
          success: false,
          message: 'Some items are no longer available in the requested quantity',
          unavailableItems
        };
      }
      
      console.log('[DEBUG] Inventory verification successful for all items');
      return { success: true };
    } catch (err) {
      console.error('[DEBUG] Error in verifyInventoryForCheckout:', err);
      
      // Create a detailed error message
      let errorDetails = '';
      if (err && err.message) {
        errorDetails = err.message;
        // Clean up common error messages for user display
        if (errorDetails.includes('indexOf')) {
          errorDetails = 'Invalid variant format in cart item';
        }
      } else {
        errorDetails = 'Unknown error';
      }
      
      return {
        success: false,
        message: 'Error checking inventory: ' + errorDetails,
        error: err
      };
    }
  };
  
  // Checkout process
  const checkout = async (deliveryAddress, phoneNumber, paymentMethod, deliveryInstructions = '') => {
    console.log('[DEBUG] Checkout initiated with:', {
      hasUser: !!user,
      userId: user?.uid,
      userEmail: user?.email,
      cartItemsCount: cartItems?.length || 0,
      deliveryAddress: !!deliveryAddress,
      phoneNumber: !!phoneNumber,
      paymentMethod,
      deliveryInstructions
    });

    if (!user || cartItems.length === 0) {
      console.error('[DEBUG] Checkout failed: No user or empty cart', {
        user: !!user,
        cartLength: cartItems?.length || 0
      });
      return { success: false, message: 'No items in cart' };
    }
    
    // First verify inventory
    console.log('[DEBUG] Verifying inventory before checkout');
    const verifyResult = await verifyInventoryForCheckout();
    if (!verifyResult.success) {
      console.error('[DEBUG] Inventory verification failed:', verifyResult.message);
      return verifyResult;
    }

    // Test Firestore permissions before proceeding
    console.log('[DEBUG] Checking user authentication state...');
    const authState = await debugUserAuthState(user);
    if (!authState.authenticated) {
      console.error('[DEBUG] User authentication failed:', authState.message);
      return {
        success: false,
        message: 'Authentication failed: ' + authState.message,
        error: authState.error
      };
    }
    console.log('[DEBUG] User authentication verified:', authState.message);
    
    console.log('[DEBUG] Ensuring user document exists...');
    const userDocResult = await createUserDocumentIfMissing(user);
    if (!userDocResult.success) {
      console.error('[DEBUG] Failed to ensure user document exists:', userDocResult.message);
      return {
        success: false,
        message: 'User setup issue: ' + userDocResult.message,
        error: userDocResult.message
      };
    }
    
    console.log('[DEBUG] Checking user type before checkout');
    const userType = await checkUserType(user);
    console.log('[DEBUG] User type from database:', userType);
    
    console.log('[DEBUG] Testing Firestore permissions...');
    const permissionTest = await testFirestorePermissions(user);
    if (!permissionTest.success) {
      console.error('[DEBUG] Permission test failed:', permissionTest.message);
      return {
        success: false,
        message: 'Authentication issue: ' + permissionTest.message,
        error: permissionTest.message
      };
    }
    console.log('[DEBUG] Permission test passed, proceeding with checkout');
    
    try {
      console.log('[DEBUG] Creating batch for checkout');
      const batch = writeBatch(db);
      const storeRef = 'dhanaura';
      const orderId = `order_${Date.now()}_${user.uid.substring(0, 5)}`;
      
      // Order items that will be saved to the order
      const orderItems = [];
      
      // Process each cart item
      for (const item of cartItems) {
        console.log(`[DEBUG] Processing item for checkout: ${item.name}, variant: ${item.variant}, qty: ${item.qty}`);
        
        // Get the product document
        const prodRef = doc(db, 'store', storeRef, 'groups', item.group, 'subgroups', item.subgroup, 'products', item.id);
        const prodSnap = await getDoc(prodRef);
        
        if (!prodSnap.exists()) {
          console.error(`[DEBUG] Product no longer exists during checkout: ${item.name}`);
          return { success: false, message: `Product "${item.name}" no longer exists` };
        }
        
        const prodData = prodSnap.data();
        const isLooseItem = prodData.loose_item || false;
        console.log(`[DEBUG] Item is loose item: ${isLooseItem}`);
        
        // Create item copy for the order record
        const orderItem = {
          id: item.id,
          name: item.name,
          variant: item.variant,
          qty: parseFloat(item.qty) || 0,
          price: parseFloat(item.price) || 0,
          mrp: parseFloat(item.mrp) || 0,
          group: item.group,
          subgroup: item.subgroup,
          image: item.image || '',
          loose_item: isLooseItem
        };
        
        // Update inventory
        if (isLooseItem) {
          // For loose items, we decrement from the main available field
          const variantWeight = extractWeightFromVariant(item.variant);
          const variantUnit = prodData.variants && prodData.variants[item.variant] && prodData.variants[item.variant].unit
            ? prodData.variants[item.variant].unit
            : extractUnitFromVariant(item.variant);
            
          const productUnit = prodData.availableUnit || 'gram';
          let totalRequestedWeight = variantWeight * parseFloat(item.qty);
          
          // Convert to product unit if needed
          if (variantUnit !== productUnit) {
            totalRequestedWeight = convertWeight(totalRequestedWeight, variantUnit, productUnit);
          }
          
          // Get current available amount
          let currentAvailable = parseFloat(prodData.available) || 0;
          
          // Calculate new available value
          const newAvailable = Math.max(0, currentAvailable - totalRequestedWeight);
          
          console.log(`[DEBUG] Updating loose item inventory: ${currentAvailable} ${productUnit} - ${totalRequestedWeight} ${productUnit} = ${newAvailable} ${productUnit}`);
          console.log(`[DEBUG] Product document path:`, prodRef.path);
          
          // Update product document with proper error handling
          try {
            batch.update(prodRef, {
              available: newAvailable,
              updatedAt: serverTimestamp()
            });
            console.log(`[DEBUG] Successfully added loose item update to batch`);
          } catch (batchErr) {
            console.error(`[DEBUG] Error adding loose item update to batch:`, batchErr);
            throw new Error(`Failed to update inventory for ${item.name}: ${batchErr.message}`);
          }
          
          // Add unit information to the order item
          orderItem.weight = variantWeight;
          orderItem.unit = variantUnit;
          orderItem.totalWeight = variantWeight * parseFloat(item.qty);
          orderItem.availableUnit = productUnit;
        } else {
          // Non-loose item
          const qty = parseFloat(item.qty) || 0;
          let currentAvailable;
          
          // Check if this is a variant product
          if (prodData.variants && prodData.variants[item.variant]) {
            currentAvailable = parseFloat(prodData.variants[item.variant].available) || 0;
            const newAvailable = Math.max(0, currentAvailable - qty);
            
            console.log(`[DEBUG] Updating variant product inventory: ${currentAvailable} - ${qty} = ${newAvailable}`);
            console.log(`[DEBUG] Product document path:`, prodRef.path);
            console.log(`[DEBUG] Variant field path: variants.${item.variant}.available`);
            
            // Create the field path for this variant's available field
            const fieldPath = `variants.${item.variant}.available`;
            
            // Validate that the variant exists in the product data
            if (!prodData.variants[item.variant]) {
              throw new Error(`Variant ${item.variant} does not exist for product ${item.name}`);
            }
            
            // Use dot notation to update nested field
            const updateObj = {};
            updateObj[fieldPath] = newAvailable;
            updateObj.updatedAt = serverTimestamp();
            
            try {
              batch.update(prodRef, updateObj);
              console.log(`[DEBUG] Successfully added variant update to batch`);
            } catch (batchErr) {
              console.error(`[DEBUG] Error adding variant update to batch:`, batchErr);
              throw new Error(`Failed to update variant inventory for ${item.name}: ${batchErr.message}`);
            }
          } else {
            // Standard product (non-variant, non-loose)
            currentAvailable = parseFloat(prodData.available) || 0;
            const newAvailable = Math.max(0, currentAvailable - qty);
            
            console.log(`[DEBUG] Updating standard product inventory: ${currentAvailable} - ${qty} = ${newAvailable}`);
            console.log(`[DEBUG] Product document path:`, prodRef.path);
            
            try {
              batch.update(prodRef, {
                available: newAvailable,
                updatedAt: serverTimestamp()
              });
              console.log(`[DEBUG] Successfully added standard product update to batch`);
            } catch (batchErr) {
              console.error(`[DEBUG] Error adding standard product update to batch:`, batchErr);
              throw new Error(`Failed to update inventory for ${item.name}: ${batchErr.message}`);
            }
          }
        }
        
        orderItems.push(orderItem);
      }
      
      // Get user's name from profile if available
      let userName = 'Anonymous';
      try {
        userName = await getUserName(user.uid);
        console.log(`[DEBUG] Got user name from profile: ${userName}`);
      } catch (nameErr) {
        console.error("[DEBUG] Error getting user name:", nameErr);
        // Fallback to values from auth object
        userName = user.name || user.displayName || user.email || 'Anonymous';
      }

      // Create the order document
      const orderData = {
        id: orderId,
        userId: user.uid,
        userEmail: user.email,
        userName: userName,
        items: orderItems,
        status: 'pending',
        total: calculateTotal(),
        createdAt: serverTimestamp(),
        deliveryAddress: deliveryAddress || '',
        phoneNumber: phoneNumber || '',
        paymentMethod: paymentMethod || 'cash_on_delivery',
        paymentStatus: 'pending',
        deliveryInstructions: deliveryInstructions || '',
        deliveryPersonId: null // Ensure new orders are available for delivery
      };
      
      // DEPRECATED: Add order to checkout collection 
      // const checkoutOrderRef = doc(db, 'checkout', user.uid);
      // console.log(`[DEBUG] Checkout order document path:`, checkoutOrderRef.path);
      // try {
      //   const checkoutData = {
      //     userId: user.uid,
      //     userEmail: user.email,
      //     lastUpdated: serverTimestamp(),
      //     [orderId]: orderData
      //   };
      //   batch.set(checkoutOrderRef, checkoutData, { merge: true });
      //   console.log(`[DEBUG] Successfully added order to checkout collection`);
      // } catch (batchErr) {
      //   console.error(`[DEBUG] Error adding order to checkout collection:`, batchErr);
      //   throw new Error(`Failed to create checkout order: ${batchErr.message}`);
      // }
      
      // Add order to the main 'orders' collection
      const orderRef = doc(db, 'orders', orderId);
      console.log(`[DEBUG] Main order document path:`, orderRef.path);
      try {
        batch.set(orderRef, orderData);
        console.log(`[DEBUG] Successfully added order to main 'orders' collection`);
      } catch (batchErr) {
        console.error(`[DEBUG] Error adding order to 'orders' collection:`, batchErr);
        throw new Error(`Failed to create order: ${batchErr.message}`);
      }
      
      // Commit the batch
      console.log('[DEBUG] Committing checkout batch with operations:', {
        productUpdates: cartItems.length,
        newOrder: 1,
        totalOperations: cartItems.length + 1
      });
      
      try {
        await batch.commit();
        console.log('[DEBUG] Batch commit successful');
        
        // Clear cart from Firebase after successful checkout
        try {
          const cartRef = doc(db, 'incart', user.uid);
          await setDoc(cartRef, { items: {} });
          console.log('[DEBUG] Firebase cart cleared successfully');
        } catch (cartClearErr) {
          console.warn('[DEBUG] Warning: Could not clear Firebase cart:', cartClearErr);
          // Don't fail the checkout if cart clearing fails
        }
        
      } catch (commitErr) {
        console.error('[DEBUG] Batch commit failed:', commitErr);
        console.error('[DEBUG] Commit error details:', {
          code: commitErr.code,
          message: commitErr.message,
          stack: commitErr.stack
        });
        
        // Check if it's an authentication issue
        if (commitErr.code === 'permission-denied' || commitErr.message.includes('permission')) {
          // Try to check if user is still authenticated
          console.log('[DEBUG] Checking user auth state after permission error...');
          console.log('[DEBUG] User object:', {
            uid: user?.uid,
            email: user?.email,
            accessToken: user?.accessToken ? 'present' : 'missing',
            emailVerified: user?.emailVerified
          });
          
          throw new Error(`Permission denied: You may need to log out and log back in. Error: ${commitErr.message}`);
        } else if (commitErr.code === 'not-found' || commitErr.message.includes('not found')) {
          throw new Error(`Product not found: One or more products in your cart no longer exist.`);
        } else if (commitErr.code === 'failed-precondition') {
          throw new Error(`Database conflict: Please try again. Some inventory may have changed.`);
        } else {
          throw new Error(`Database error: ${commitErr.message || 'Unknown error during checkout'}`);
        }
      }
      
      // Clear cart after successful checkout
      setCartItems([]);
      localStorage.removeItem('cartItems');
      
      console.log('[DEBUG] Checkout successful, order ID:', orderId);
      return {
        success: true,
        message: 'Order placed successfully',
        orderId
      };
    } catch (err) {
      console.error('[DEBUG] Error during checkout:', err);
      return {
        success: false,
        message: 'Error during checkout: ' + (err.message || 'Unknown error'),
        error: err.message
      };
    }
  };
  
  const updateOrderStatus = async (orderId, newStatus) => {
    if (!user || userType === 'customer') {
      return { success: false, message: 'Permission denied.' };
    }
    
    console.log(`[DEBUG] Updating order status: ${orderId} to ${newStatus}`);
    
    try {
      // Handle cancelled status
      if (newStatus === 'cancelled') {
        console.log(`[DEBUG] Order ${orderId} being cancelled by delivery person`);
        return await cancelOrder(orderId, false, true); // Pass true as third parameter to indicate delivery cancellation
      }

      // Standard status update for all statuses (including 'delivered')
      const orderRef = doc(db, 'orders', orderId);
      
      const orderSnap = await getDoc(orderRef);
      if (!orderSnap.exists()) {
        return { success: false, message: 'Order not found.' };
      }

      const orderData = orderSnap.data();
      const updateData = {
        status: newStatus,
        updatedAt: serverTimestamp()
      };

      // Add delivery completion timestamp for delivered orders
      if (newStatus === 'delivered') {
        updateData.deliveredAt = serverTimestamp();
        console.log(`[DEBUG] Order ${orderId} marked as delivered - keeping in orders collection`);
      }

      // Security check for delivery personnel
      if (userType === 'delivery' && orderData.status !== 'pending' && !isAssignedTo(orderData, user.uid)) {
        return { success: false, message: 'This order is not assigned to you.' };
      }

      await updateDoc(orderRef, updateData);
      console.log(`[DEBUG] Order ${orderId} status updated to ${newStatus} successfully`);
      return { success: true, message: `Order status updated to ${newStatus}` };

    } catch (err) {
      console.error(`[DEBUG] Error updating order status:`, err);
      return { success: false, message: 'Error updating order status: ' + err.message };
    }
  };

  // Cancel order and return inventory (no longer moves to history collection)
  const cancelOrder = async (orderId, isAdmin = false, isCancelledByDelivery = false) => {
    try {
      console.log(`[DEBUG] Starting cancel order process for order: ${orderId}, isAdmin: ${isAdmin}, isCancelledByDelivery: ${isCancelledByDelivery}`);
      
      if (!user) {
        console.error("[DEBUG] No authenticated user");
        return { success: false, message: 'User not authenticated' };
      }
      
      console.log(`[DEBUG] User attempting to cancel order:`, {
        uid: user.uid,
        email: user.email,
        userType: userType
      });
      
      // Get the order from the 'orders' collection
      const orderRef = doc(db, 'orders', orderId);
      const orderSnap = await getDoc(orderRef);
      
      if (!orderSnap.exists()) {
        console.error(`[DEBUG] Order ${orderId} not found in 'orders' collection`);
        return { success: false, message: 'Order not found.' };
      }
      
      const orderData = orderSnap.data();
      console.log(`[DEBUG] Found order data:`, {
        id: orderId,
        userId: orderData.userId,
        status: orderData.status,
        itemCount: orderData.items?.length || 0
      });

      // Security check: ensure user can only cancel their own order unless they are an admin or delivery person
      if (!isAdmin && !isCancelledByDelivery && orderData.userId !== user.uid) {
        console.error(`[DEBUG] User ${user.uid} attempted to cancel order ${orderId} owned by ${orderData.userId}`);
        return { success: false, message: "You don't have permission to cancel this order." };
      }

      // A delivery person can only cancel an order assigned to them that is not yet delivered
      if (userType === 'delivery' && !isCancelledByDelivery && (!isAssignedTo(orderData, user.uid) || orderData.status === 'delivered')) {
        return { success: false, message: "You cannot cancel this order." };
      }

      console.log(`[DEBUG] Updating order status to cancelled`);
      
      try {
        // Update order data with cancellation info
        const updateData = {
          status: 'cancelled',
          updatedAt: serverTimestamp(),
          cancelledAt: serverTimestamp()
        };
        
        // Add information about who cancelled the order
        if (isAdmin) {
          updateData.cancelledBy = 'admin';
          updateData.cancellationNote = 'Cancelled by administrator';
        } else if (isCancelledByDelivery) {
          updateData.cancelledBy = 'delivery';
          // Try to get user name from profile
          let deliveryPersonName = '';
          try {
            deliveryPersonName = await getUserName(user.uid);
          } catch (nameErr) {
            console.error("[DEBUG] Error getting delivery person name:", nameErr);
            deliveryPersonName = user.name || user.email || user.uid;
          }
          updateData.cancellationNote = `Cancelled by delivery person: ${deliveryPersonName}`;
          updateData.deliveryPersonIdCancelled = user.uid;
        } else {
          updateData.cancelledBy = 'customer';
          updateData.cancellationNote = 'Cancelled by customer';
        }
        
        // Update the order document (keeping it in orders collection)
        await updateDoc(orderRef, updateData);
        console.log(`[DEBUG] Successfully updated order status to cancelled`);
        
        // Return inventory
        console.log(`[DEBUG] Returning inventory for ${orderData.items?.length || 0} items`);
        try {
          for (const item of orderData.items) {
            const storeRef = 'dhanaura'; // Assuming a single store
            const prodRef = doc(db, 'store', storeRef, 'groups', item.group, 'subgroups', item.subgroup, 'products', item.id);
            
            // Use increment to atomically update inventory
            if (item.loose_item) {
              let weightToReturn = item.totalWeight || (extractWeightFromVariant(item.variant) * parseFloat(item.qty));
              const productUnit = (await getDoc(prodRef)).data()?.availableUnit || 'gram';
              if (item.unit && item.unit !== productUnit) {
                weightToReturn = convertWeight(weightToReturn, item.unit, productUnit);
              }
              await updateDoc(prodRef, { available: increment(weightToReturn) });
            } else {
              if (item.hasVariants) {
                await updateDoc(prodRef, { [`variants.${item.variant}.available`]: increment(item.qty) });
              } else {
                await updateDoc(prodRef, { available: increment(item.qty) });
              }
            }
          }
        } catch (inventoryErr) {
          console.error(`[DEBUG] Error returning inventory:`, inventoryErr);
          console.warn(`[DEBUG] Order cancelled but inventory might not have been properly returned: ${inventoryErr.message}`);
        }
        
        console.log(`[DEBUG] Order ${orderId} successfully cancelled and kept in orders collection`);
        return { success: true, message: 'Order cancelled successfully' };
        
      } catch (updateError) {
        console.error('[DEBUG] Error updating order during cancellation:', updateError);
        throw new Error(`Failed to update order: ${updateError.message}`);
      }

    } catch (err) {
      console.error('[DEBUG] Error during order cancellation:', err);
      console.error('[DEBUG] Error details:', {
        code: err.code,
        message: err.message,
        stack: err.stack
      });
      return { success: false, message: 'Error cancelling order: ' + (err.message || 'Unknown error') };
    }
  };

  // DEPRECATED: Function no longer used - orders now stay in 'orders' collection with status updates
  // Keeping for reference in case of rollback needed
  /*
  const markOrderAsDelivered = async (orderId) => {
    try {
      console.log(`[DEBUG] Marking order as delivered: ${orderId}`);
      
      const orderRef = doc(db, 'orders', orderId);
      const orderSnap = await getDoc(orderRef);

      if (!orderSnap.exists()) {
        console.error(`[DEBUG] Order ${orderId} not found in 'orders' collection.`);
        return { success: false, message: 'Order not found or already processed.' };
      }

      const orderData = orderSnap.data();
      
      // Perform operations in sequence for better error tracing
      try {
        // 1. Create parent orderHistory document if it doesn't exist
        const userId = orderData.userId;
        const userHistoryRef = doc(db, 'orderHistory', userId);
        
        try {
          await setDoc(userHistoryRef, { 
            userId: userId,
            lastUpdated: serverTimestamp()
          }, { merge: true });
          
          console.log(`[DEBUG] Successfully created/updated parent history document at: ${userHistoryRef.path}`);
        } catch (parentErr) {
          console.error(`[DEBUG] Error creating parent history document:`, parentErr);
          throw new Error(`Failed to create parent history document: ${parentErr.message}`);
        }
        
        // 2. Create the order history document
        const historyRef = doc(db, 'orderHistory', userId, 'orders', orderId);
        
        try {
          await setDoc(historyRef, {
            ...orderData,
            status: 'delivered',
            updatedAt: serverTimestamp()
          });
          
          console.log(`[DEBUG] Successfully created order history at: ${historyRef.path}`);
        } catch (historyErr) {
          console.error(`[DEBUG] Error creating order history:`, historyErr);
          throw new Error(`Failed to create order history: ${historyErr.message}`);
        }
        
        // 3. Delete the original order
        try {
          await deleteDoc(orderRef);
          console.log(`[DEBUG] Successfully deleted original order: ${orderRef.path}`);
        } catch (deleteErr) {
          console.error(`[DEBUG] Error deleting original order:`, deleteErr);
          throw new Error(`Failed to delete original order: ${deleteErr.message}`);
        }

        console.log(`[DEBUG] Order ${orderId} successfully marked as delivered and moved to history.`);
        return { success: true, message: 'Order marked as delivered.' };
        
      } catch (stepError) {
        console.error('[DEBUG] Specific step failed during delivery marking:', stepError);
        throw new Error(`Delivery marking failed: ${stepError.message}`);
      }

    } catch (err) {
      console.error(`[DEBUG] Error marking order as delivered:`, err);
      return { success: false, message: 'Error processing order: ' + err.message };
    }
  };
  */
  
  // Assign order to self (for delivery personnel)
  const assignOrderToSelf = async (orderId) => {
    if (!user || !user.uid) {
      return { success: false, message: 'You must be logged in.' };
    }

    console.log(`[DEBUG] Delivery person ${user.uid} attempting to accept order ${orderId}`);
    
    const orderRef = doc(db, 'orders', orderId);

    try {
      await updateDoc(orderRef, {
        deliveryPersonId: user.uid,
        status: 'accepted',
        updatedAt: serverTimestamp()
      });

      console.log(`[DEBUG] Order ${orderId} successfully assigned to ${user.uid}`);
      return { success: true, message: 'Order accepted.' };
    } catch (err) {
      console.error(`[DEBUG] Error assigning order:`, err);
      return { success: false, message: `Error assigning order: ${err.message}` };
    }
  };

  // Simple addToCart utility function for internal use
  const addItemDirectly = (item) => {
    if (!item) return;
    
    // Ensure all numeric values are parsed as numbers
    const itemToAdd = {
      ...item,
      price: parseFloat(item.price) || 0,
      mrp: parseFloat(item.mrp) || 0,
      qty: parseFloat(item.qty) || 1
    };
    
    console.log(`[DEBUG] Directly adding item to cart:`, itemToAdd);
    
    setCartItems(prevItems => {
      // Check if item already exists in cart
      const itemIndex = prevItems.findIndex(i => 
        i.id === itemToAdd.id && i.variant === itemToAdd.variant
      );
      
      // If item exists, update quantity
      if (itemIndex !== -1) {
        const newCartItems = [...prevItems];
        const existingQty = parseFloat(newCartItems[itemIndex].qty) || 0;
        const newQty = existingQty + (parseFloat(itemToAdd.qty) || 1);
        
        console.log(`[DEBUG] Updating quantity for existing cart item: ${existingQty} + ${itemToAdd.qty} = ${newQty}`);
        
        newCartItems[itemIndex] = {
          ...newCartItems[itemIndex],
          qty: newQty
        };
        
        // Save to local storage
        localStorage.setItem('cartItems', JSON.stringify(newCartItems));
        
        return newCartItems;
      } else {
        // Add as new item
        const newCartItems = [...prevItems, itemToAdd];
        
        // Save to local storage
        localStorage.setItem('cartItems', JSON.stringify(newCartItems));
        
        return newCartItems;
      }
    });
  };
  
  // Helper function to get username from profile if available
  const getUserName = async (userId) => {
    try {
      // Check if user profile exists in Firestore
      const userDocRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userDocRef);
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        // Return the name from profile if available
        return userData.name || userData.userName || 'Anonymous';
      }
      return 'Anonymous';
    } catch (error) {
      console.error('Error fetching user profile for name:', error);
      return 'Anonymous';
    }
  };
  
  // Calculate cart total including delivery and handling fees
  const calculateTotal = () => {
    if (!cartItems || cartItems.length === 0) {
      return 0;
    }
    
    // Calculate subtotal
    const subtotal = cartItems.reduce((total, item) => {
      const qty = parseFloat(item.qty) || 0;
      const price = parseFloat(item.price) || 0;
      return total + (qty * price);
    }, 0);
    
    // Calculate delivery info to get accurate fees
    const deliveryInfo = calculateDeliveryInfo(cartItems, subtotal);
    
    // Return total with delivery and handling fees
    return subtotal + deliveryInfo.actualDeliveryFee + deliveryInfo.actualHandlingFee;
  };
  
  // Input validation utilities
const validateQuantity = (quantity) => {
  const parsed = parseFloat(quantity);
  return !isNaN(parsed) && parsed > 0 && parsed <= 1000; // Max 1000 units
};

const sanitizeInput = (input) => {
  if (typeof input === 'string') {
    return input.trim().slice(0, 500); // Limit string length
  }
  return input;
};

const validatePhoneNumber = (phone) => {
  return typeof phone === 'string' && /^[6-9]\d{9}$/.test(phone);
};

  return (
    <CartContext.Provider
      value={{
        cartItems,
        loading,
        error,
        isOnline,
        addToCart,
        addItemDirectly,
        updateCartQuantity,
        removeFromCart,
        checkout,
        verifyInventoryForCheckout,
        cancelOrder,
        assignOrderToSelf,
        updateOrderStatus,
        inventoryLevels,
        checkAvailability,
        calculateTotal,
        extractWeightFromVariant,
        extractUnitFromVariant,
        convertWeight
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);